import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';

/*
 * Some tools need a detail before they can connect: a Jira site, a Shopify
 * store and its access token. Every Connect button goes through one API call,
 * so the form is opened from there and answers with what was typed, or null
 * when the person closes it.
 */

export interface ConnectField { name: string; label: string; description: string; secret: boolean }

/* Wording for the one-time sign-in setup (an OAuth app's client id and
   secret) instead of the everyday connect form. */
export interface ConnectFieldsCopy { title: string; intro: string; submit: string; note?: string }

const INPUT = 'h-[42px] w-full rounded-[8px] border border-[#dedede] bg-white px-3 text-[14px] text-[#18191d] outline-none placeholder:text-[#92949d] focus:border-[#18191d]';

const FieldsDialog: React.FC<{
  toolLabel: string;
  fields: ConnectField[];
  copy?: ConnectFieldsCopy;
  onDone: (values: Record<string, string> | null) => void;
}> = ({ toolLabel, fields, copy, onDone }) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const ready = fields.every((f) => (values[f.name] || '').trim());

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    onDone(values);
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-[460px] space-y-4 rounded-[14px] bg-white p-6 shadow-xl">
        <div>
          <h2 className="m-0 text-[19px] font-bold text-[#18191d]">{copy?.title || `Connect ${toolLabel}`}</h2>
          <p className="m-0 mt-1.5 text-[13.5px] leading-[1.55] text-[#666971]">{copy?.intro || `${toolLabel} needs these details before it can connect.`}</p>
          {copy?.note && <p className="m-0 mt-2 break-all rounded-[8px] bg-[#f4f4f5] px-3 py-2 font-mono text-[12px] text-[#18191d]">{copy.note}</p>}
        </div>
        {fields.map((f, i) => (
          <div key={f.name}>
            <label htmlFor={`connect-field-${f.name}`} className="mb-1.5 block text-[13px] font-semibold text-[#18191d]">{f.label}</label>
            <input
              id={`connect-field-${f.name}`}
              type={f.secret ? 'password' : 'text'}
              autoComplete="off"
              autoFocus={i === 0}
              className={INPUT}
              value={values[f.name] || ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
            />
            {f.description && <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[#777982]">{f.description}</p>}
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={() => onDone(null)} className="h-[38px] rounded-[8px] border border-[#dedede] bg-white px-4 text-[13.5px] font-semibold text-[#18191d]">Cancel</button>
          <button type="submit" disabled={!ready} className="h-[38px] rounded-[8px] bg-[#18191d] px-4 text-[13.5px] font-semibold text-white disabled:opacity-50">{copy?.submit || 'Connect'}</button>
        </div>
      </form>
    </div>
  );
};

export const askConnectFields = (
  toolLabel: string,
  fields: ConnectField[],
  copy?: ConnectFieldsCopy,
): Promise<Record<string, string> | null> =>
  new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (values: Record<string, string> | null) => {
      root.unmount();
      host.remove();
      resolve(values);
    };
    root.render(<FieldsDialog toolLabel={toolLabel} fields={fields} copy={copy} onDone={done} />);
  });
